import { useState, useEffect, Suspense } from "react";
import { T, buildNavItems } from "./constants";
import { useAuth } from "./hooks/useAuth";
import { useSubdomain } from "./hooks/useSubdomain";
import { useAppData } from "./hooks/useAppData";
import { useTeacherCheckIn } from "./hooks/useTeacherCheckIn";
import usePushNotification from "./hooks/usePushNotification";
import { ADMIN_ROLES, isAdmin } from "./constants/roles";

import AppShell from "./components/layout/AppShell";
import { ThemeProvider } from "./context/ThemeContext";
import { SchoolProvider } from "./context/SchoolContext";
import { ChatProvider } from "./context/ChatContext";
import {
  AdminRoutes,
  TeacherRoutes,
  StudentRoutes,
  TutorRoutes,
  IndividualRoutes,
  PlaceholderPage,
} from "./components/routes";
import ParentDashboard from "./views/parent/ParentDashboard";

function LazyFallback() {
  return (
    <div className="flex items-center justify-center h-[300px]">
      <span className="w-6 h-6 border-2 border-navy-200 border-t-navy-600 rounded-full animate-spin" />
    </div>
  );
}

function NotificationBanner({ onAllow }) {
  return (
    <div className="fixed bottom-4 right-4 z-50 bg-white rounded-2xl shadow-lg border border-navy-100 p-4 flex items-center gap-3 max-w-sm">
      <span className="text-2xl">🔔</span>
      <div className="flex-1">
        <div className="font-sora text-sm font-bold text-navy-800">Enable Notifications</div>
        <div className="font-body text-xs text-navy-400">Get instant alerts for attendance, fees & more</div>
      </div>
      <button
        onClick={onAllow}
        className="shrink-0 rounded-xl bg-brand-400 px-3 py-1.5 text-xs font-bold text-white"
      >
        Allow
      </button>
    </div>
  );
}

export default function AppRouter({ onLogout }) {
  const [lang, setLang] = useState("en");
  const [nav, setNav] = useState(() => {
    // Check if user is saas_admin from localStorage on initial load
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        return user.role === 'saas_admin' ? 'superadmin' : 'dashboard';
      } catch (e) {
        return 'dashboard';
      }
    }
    return 'dashboard';
  });
  const [tutorStudents, setTutorStudents] = useState([]);
  const [tutorHomework, setTutorHomework] = useState([]);

  const { user, patchUser } = useAuth();
  const { permission, requestPermission } = usePushNotification();

  const t = T[lang];
  const navItems = buildNavItems(t);

  // Get nav items key based on user role
  const getNavItemsKey = (role) => {
    if (!role) return null;
    if (role === "saas_admin") return "saas_admin";
    if (isAdmin(role)) return "admin";
    return role;
  };

  const navItemsKey = getNavItemsKey(user?.role);

  // Debug logging for nav items
  useEffect(() => {
    console.log("User role:", user?.role);
    console.log("Is admin:", isAdmin(user?.role));
    console.log("Nav items key:", navItemsKey);
    console.log("Nav items count:", navItems[navItemsKey]?.length || 0);
    console.log("Nav items:", navItems[navItemsKey]);
  }, [user, navItems, navItemsKey]);

  const {
    stuData, setStuData,
    teachData, setTeachData,
    curriculum, setCurriculum,
    tests, setTests,
    homework, setHomework,
    teacherLogs, setTeacherLogs,
    timetable, setTimetable,
    fetchAllData,
  } = useAppData(user, patchUser);

  const { handleTeacherCheckIn } = useTeacherCheckIn(user, teachData, setTeachData, patchUser);

  // Redirect saas_admin from dashboard to superadmin (since they don't have school-specific dashboard)
  useEffect(() => {
    if (user?.role === 'saas_admin' && nav === 'dashboard') {
      setNav('superadmin');
    }
  }, [user?.role, nav, setNav]);

  // Fetch data once when user identity changes
  useEffect(() => {
    if (user?._id) fetchAllData();
  }, [user?._id, fetchAllData]);

  const renderContent = () => {
    if (!user) return null;

    if (user.role === 'saas_admin') {
      // saas_admin has no school — never render school-specific AdminDashboard
      const saasNav = nav === 'dashboard' ? 'superadmin' : nav;
      return (
        <AdminRoutes
          nav={saasNav} t={t} setNav={setNav} lang={lang} user={user}
          stuData={stuData} setStuData={setStuData}
          teachData={teachData} setTeachData={setTeachData}
          teacherLogs={teacherLogs} setTeacherLogs={setTeacherLogs}
          timetable={timetable} setTimetable={setTimetable}
          tests={tests} fetchAllData={fetchAllData}
        />
      );
    }

    if (isAdmin(user.role)) {
      return (
        <AdminRoutes
          nav={nav} t={t} setNav={setNav} lang={lang} user={user}
          stuData={stuData} setStuData={setStuData}
          teachData={teachData} setTeachData={setTeachData}
          teacherLogs={teacherLogs} setTeacherLogs={setTeacherLogs}
          timetable={timetable} setTimetable={setTimetable}
          tests={tests} fetchAllData={fetchAllData}
        />
      );
    }

    if (user.role === "teacher" || user.role === "coordinator") {
      return (
        <TeacherRoutes
          nav={nav} t={t} lang={lang} user={user} setNav={setNav}
          stuData={stuData} setStuData={setStuData}
          teachData={teachData} setTeachData={setTeachData}
          curriculum={curriculum} setCurriculum={setCurriculum}
          tests={tests} setTests={setTests}
          homework={homework} setHomework={setHomework}
          teacherLogs={teacherLogs} setTeacherLogs={setTeacherLogs}
          timetable={timetable}
          onCheckIn={handleTeacherCheckIn}
          fetchAllData={fetchAllData}
        />
      );
    }

    if (user.role === "student") {
      return (
        <StudentRoutes
          nav={nav} setNav={setNav} user={user}
          stuData={stuData} tests={tests}
          curriculum={curriculum} homework={homework} timetable={timetable}
        />
      );
    }

    if (user.role === "tutor") {
      return (
        <TutorRoutes
          nav={nav} user={user}
          students={tutorStudents} setStudents={setTutorStudents}
          homework={tutorHomework} setHomework={setTutorHomework}
        />
      );
    }

    if (user.role === "individual") {
      return <IndividualRoutes nav={nav} setNav={setNav} user={user} />;
    }

    if (user.role === "parent") {
      return <ParentDashboard nav={nav} setNav={setNav} user={user} />;
    }

    return <PlaceholderPage label="Role not supported" />;
  };

  return (
    <ThemeProvider>
      <SchoolProvider user={user}>
      <ChatProvider user={user}>
      <AppShell
        nav={nav}
        setNav={setNav}
        navItems={navItems[navItemsKey] || []}
        user={user}
        lang={lang}
        setLang={setLang}
        onLogout={onLogout}
      >
        {permission === "default" && (
          <NotificationBanner onAllow={requestPermission} />
        )}
        <Suspense fallback={<LazyFallback />}>
          {renderContent()}
        </Suspense>
      </AppShell>
      </ChatProvider>
      </SchoolProvider>
    </ThemeProvider>
  );
}